import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Flame, LogOut, CalendarDays, Settings, Plus, Trash2, Pencil, X, Check, Upload, ExternalLink, RefreshCw, Image, FileText, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SiteContentTab } from '../components/SiteContentTab';
import { SeriesTab } from '../components/SeriesTab';
import { FloorPlanTab } from '../components/FloorPlanTab';
import { IcmTab } from '../components/IcmTab';
import { VideosTab } from '../components/VideosTab';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAME_TYPES = ['NL Hold\'em', 'PL Omaha', 'Mixed Games', 'PL 5 Draw', 'Stud Poker', 'Draw Poker'];
const TAG_OPTIONS = ['M.E.', 'K.O.', 'Multi day', 'Turbo', 'Hyper Turbo', 'Super Hyper Turbo', 'Satellite', 'Stack Choice'];

const emptyTournament = {
  date: '', time: '', event_number: '', event_name: '', buyin: '', buyin_breakdown: '',
  bounty: '', guaranteed: '', stack: '', duration: '', reg_close: '',
  is_premium: false, game_type: 'NL Hold\'em', tags: [],
};

export default function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedDate, setSelectedDate] = useState('all');
  const [dates, setDates] = useState([]);
  const [editingTournament, setEditingTournament] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ...emptyTournament });
  const [saving, setSaving] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const token = user?.token;

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTournaments = useCallback(async () => {
    try {
      const params = selectedDate !== 'all' ? { date: selectedDate } : {};
      const { data } = await axios.get(`${API}/tournaments`, { params });
      setTournaments(data);
    } catch (e) { console.error(e); }
  }, [selectedDate]);

  const fetchDates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/dates`);
      setDates(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchDates(); fetchSettings(); }, [fetchDates, fetchSettings]);
  useEffect(() => { fetchTournaments(); }, [fetchTournaments]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch (e) { console.warn('Server logout failed, clearing local session anyway:', e.message); }
    onLogout();
    navigate('/admin/login');
  };

  // Tournament CRUD
  const openCreateForm = () => {
    setEditingTournament(null);
    setFormData({ ...emptyTournament, date: selectedDate !== 'all' ? selectedDate : '' });
    setShowForm(true);
  };

  const openEditForm = (t) => {
    setEditingTournament(t.id);
    setFormData({
      date: t.date || '', time: t.time || '', event_number: t.event_number || '',
      event_name: t.event_name || '', buyin: t.buyin || '', buyin_breakdown: t.buyin_breakdown || '',
      bounty: t.bounty || '', guaranteed: t.guaranteed || '', stack: t.stack || '',
      duration: t.duration || '', reg_close: t.reg_close || '', is_premium: t.is_premium || false,
      game_type: t.game_type || 'NL Hold\'em', tags: t.tags || [],
    });
    setShowForm(true);
  };

  const saveTournament = async () => {
    setSaving(true);
    try {
      const payload = { ...formData };
      // Clean empty strings to null
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' && k !== 'date' && k !== 'time' && k !== 'event_number' && k !== 'event_name') {
          payload[k] = null;
        }
      });
      if (editingTournament) {
        await axios.put(`${API}/admin/tournaments/${editingTournament}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/tournaments`, payload, { headers });
      }
      setShowForm(false);
      fetchTournaments();
      fetchDates();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const deleteTournament = async (id) => {
    if (!window.confirm('Delete this tournament?')) return;
    try {
      await axios.delete(`${API}/admin/tournaments/${id}`, { headers });
      fetchTournaments();
      fetchDates();
    } catch (e) { alert('Failed to delete'); }
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag],
    }));
  };

  // Settings
  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      const { id, ...rest } = settings;
      await axios.put(`${API}/admin/settings`, rest, { headers });
    } catch (e) {
      alert('Failed to save settings');
    } finally { setSettingsSaving(false); }
  };

  // Image upload
  const handleImageUpload = async (field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingField(field);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
        updateSetting(field, fileUrl);
      } catch (err) {
        alert('Upload failed');
      } finally { setUploadingField(null); }
    };
    input.click();
  };

  return (
    <div className="min-h-screen bg-background-base" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background-surface/95 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Flame className="w-5 h-5 text-secondary-hover" />
            <span className="font-heading text-xl font-bold text-white">
              RDPT<span className="text-primary-main">+</span> Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/admin/billboard" className="text-sm text-primary-main hover:text-primary-hover flex items-center gap-1 font-heading tracking-wider" data-testid="create-billboard-link">
              <Image className="w-3.5 h-3.5" /> Billboard
            </a>
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-primary-main flex items-center gap-1">
              <ExternalLink className="w-3.5 h-3.5" /> View Site
            </a>
            <span className="text-xs text-white/40 font-mono">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button"
              className="text-white/50 hover:text-white hover:bg-white/5">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="tournaments" className="w-full">
          <TabsList className="bg-background-surface border border-white/[0.06] mb-8">
            <TabsTrigger value="tournaments" className="data-[state=active]:bg-secondary-main data-[state=active]:text-white font-heading tracking-wider" data-testid="tab-tournaments">
              <CalendarDays className="w-4 h-4 mr-2" /> Tournaments
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-secondary-main data-[state=active]:text-white font-heading tracking-wider" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" /> Settings & Branding
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-secondary-main data-[state=active]:text-white font-heading tracking-wider" data-testid="tab-content">
              <FileText className="w-4 h-4 mr-2" /> Site Content
            </TabsTrigger>
            <TabsTrigger value="series" className="data-[state=active]:bg-secondary-main data-[state=active]:text-white font-heading tracking-wider" data-testid="tab-series">
              <CalendarDays className="w-4 h-4 mr-2" /> Series
            </TabsTrigger>
            <TabsTrigger value="floor" className="data-[state=active]:bg-secondary-main data-[state=active]:text-white font-heading tracking-wider" data-testid="tab-floor">
              <LayoutGrid className="w-4 h-4 mr-2" /> Floor Plan
            </TabsTrigger>
            <TabsTrigger value="icm" className="data-[state=active]:bg-secondary-main data-[state=active]:text-white font-heading tracking-wider" data-testid="tab-icm">
              ICM
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-secondary-main data-[state=active]:text-white font-heading tracking-wider" data-testid="tab-videos">
              <Image className="w-4 h-4 mr-2" /> Videos
            </TabsTrigger>
          </TabsList>

          {/* ========== TOURNAMENTS TAB ========== */}
          <TabsContent value="tournaments">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <Label className="text-white/60 text-sm">Filter by date:</Label>
                <Select value={selectedDate} onValueChange={setSelectedDate}>
                  <SelectTrigger className="w-[200px] bg-background-surface border-white/10 text-white" data-testid="date-filter-select">
                    <SelectValue placeholder="All dates" />
                  </SelectTrigger>
                  <SelectContent className="bg-background-surface border-white/10 text-white">
                    <SelectItem value="all">All dates</SelectItem>
                    {dates.map(d => (
                      <SelectItem key={d.date} value={d.date}>
                        {d.date} ({d.day_name}) - {d.tournament_count}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedDate !== 'all' && (
                  <Button variant="outline" size="sm"
                    onClick={() => window.open(`/?date=${selectedDate}`, '_blank')}
                    className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 font-heading tracking-wider"
                    data-testid="view-schedule-new-window">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View Schedule
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={openCreateForm} className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider" data-testid="add-tournament-button">
                  <Plus className="w-4 h-4 mr-2" /> Add Tournament
                </Button>
              <Button
                onClick={async () => {
                  if (!window.confirm('This will replace ALL tournaments with live data from PokerLens. Continue?')) return;
                  setSyncing(true);
                  try {
                    await axios.post(`${API}/admin/sync-pokerlens`, {}, { headers });
                    // Sync runs in the background (can take ~1-2 min). Poll status.
                    const poll = async () => {
                      const { data: st } = await axios.get(`${API}/admin/sync-status`, { headers });
                      if (st.running) { setTimeout(poll, 3000); return; }
                      setSyncing(false);
                      if (st.error) { alert(st.error); return; }
                      alert(st.message || 'Sync complete');
                      fetchTournaments();
                      fetchDates();
                    };
                    setTimeout(poll, 3000);
                  } catch (e) {
                    setSyncing(false);
                    alert(e.response?.data?.detail || 'Sync failed');
                  }
                }}
                disabled={syncing}
                variant="outline"
                className="border-secondary-main text-secondary-hover hover:bg-secondary-main/10 font-heading tracking-wider"
                data-testid="sync-pokerlens-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync PokerLens'}
              </Button>
              </div>
            </div>

            {/* Tournament Form Modal */}
            {showForm && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-20 px-4 overflow-y-auto">
                <div className="bg-background-surface border border-white/[0.1] rounded-xl p-6 w-full max-w-2xl mb-10" data-testid="tournament-form-modal">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-heading text-xl text-white tracking-wide uppercase">
                      {editingTournament ? 'Edit Tournament' : 'New Tournament'}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-white/50 hover:text-white">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Date */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Date *</Label>
                      <Input type="date" value={formData.date} onChange={e => setFormData(p => ({ ...p, date: e.target.value }))}
                        className="bg-background-base border-white/10 text-white" data-testid="form-date" required />
                    </div>
                    {/* Time */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Time *</Label>
                      <Input type="time" value={formData.time} onChange={e => setFormData(p => ({ ...p, time: e.target.value }))}
                        className="bg-background-base border-white/10 text-white" data-testid="form-time" required />
                    </div>
                    {/* Event # */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Event # *</Label>
                      <Input value={formData.event_number} onChange={e => setFormData(p => ({ ...p, event_number: e.target.value }))}
                        placeholder="e.g. 22, MS6" className="bg-background-base border-white/10 text-white" data-testid="form-event-number" required />
                    </div>
                    {/* Event Name */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Event Name *</Label>
                      <Input value={formData.event_name} onChange={e => setFormData(p => ({ ...p, event_name: e.target.value }))}
                        placeholder="RED DRAGON PRESTIGE HIGH ROLLER" className="bg-background-base border-white/10 text-white" data-testid="form-event-name" required />
                    </div>
                    {/* Buy-in */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">KRW Buy-in</Label>
                      <Input value={formData.buyin} onChange={e => setFormData(p => ({ ...p, buyin: e.target.value }))}
                        placeholder="8,000,000" className="bg-background-base border-white/10 text-white" data-testid="form-buyin" />
                    </div>
                    {/* Breakdown */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Prizepool Breakdown</Label>
                      <Input value={formData.buyin_breakdown} onChange={e => setFormData(p => ({ ...p, buyin_breakdown: e.target.value }))}
                        placeholder="7,280,000 + 720,000" className="bg-background-base border-white/10 text-white" data-testid="form-breakdown" />
                    </div>
                    {/* Bounty */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Bounty</Label>
                      <Input value={formData.bounty} onChange={e => setFormData(p => ({ ...p, bounty: e.target.value }))}
                        placeholder="Bounty 200,000" className="bg-background-base border-white/10 text-white" data-testid="form-bounty" />
                    </div>
                    {/* Guaranteed */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Guaranteed</Label>
                      <Input value={formData.guaranteed} onChange={e => setFormData(p => ({ ...p, guaranteed: e.target.value }))}
                        placeholder="1,800,000,000 GTD" className="bg-background-base border-white/10 text-white" data-testid="form-guaranteed" />
                    </div>
                    {/* Stack */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Stack</Label>
                      <Input value={formData.stack} onChange={e => setFormData(p => ({ ...p, stack: e.target.value }))}
                        placeholder="300,000" className="bg-background-base border-white/10 text-white" data-testid="form-stack" />
                    </div>
                    {/* Duration */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Duration</Label>
                      <Input value={formData.duration} onChange={e => setFormData(p => ({ ...p, duration: e.target.value }))}
                        placeholder="40 min" className="bg-background-base border-white/10 text-white" data-testid="form-duration" />
                    </div>
                    {/* Reg Close */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Reg Close</Label>
                      <Input value={formData.reg_close} onChange={e => setFormData(p => ({ ...p, reg_close: e.target.value }))}
                        placeholder="level 14 on day 2" className="bg-background-base border-white/10 text-white" data-testid="form-reg-close" />
                    </div>
                    {/* Game Type */}
                    <div className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Game Type</Label>
                      <Select value={formData.game_type} onValueChange={v => setFormData(p => ({ ...p, game_type: v }))}>
                        <SelectTrigger className="bg-background-base border-white/10 text-white" data-testid="form-game-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background-surface border-white/10 text-white">
                          {GAME_TYPES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Premium toggle */}
                  <div className="flex items-center gap-3 mt-5">
                    <Switch checked={formData.is_premium} onCheckedChange={v => setFormData(p => ({ ...p, is_premium: v }))} data-testid="form-premium-toggle" />
                    <Label className="text-white/80 text-sm">Premium / Highlight Event</Label>
                  </div>

                  {/* Tags */}
                  <div className="mt-5">
                    <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Tags</Label>
                    <div className="flex flex-wrap gap-2">
                      {TAG_OPTIONS.map(tag => (
                        <button key={tag} onClick={() => toggleTag(tag)} data-testid={`form-tag-${tag}`}
                          className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-all ${
                            formData.tags.includes(tag)
                              ? 'bg-primary-main text-black'
                              : 'bg-white/5 text-white/50 hover:bg-white/10'
                          }`}>{tag}</button>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/[0.06]">
                    <Button onClick={saveTournament} disabled={saving}
                      className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider"
                      data-testid="form-save-button">
                      <Check className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowForm(false)} className="text-white/50 hover:text-white">
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Tournaments Table */}
            <div className="w-full overflow-x-auto rounded-xl border border-white/[0.08] bg-background-surface" data-testid="admin-tournaments-table">
              <table className="w-full min-w-[900px] text-left border-collapse">
                <thead>
                  <tr className="bg-background-base border-b border-white/[0.08]">
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground">Date</th>
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground">Time</th>
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground">#</th>
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground">Event</th>
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground">Buy-in</th>
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground">Stack</th>
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground">Duration</th>
                    <th className="px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tournaments.map((t, i) => (
                    <tr key={t.id} className={`border-b border-white/[0.04] hover:bg-white/[0.03] ${t.is_premium ? 'bg-primary-main/5' : i % 2 === 0 ? '' : 'bg-white/[0.01]'}`}>
                      <td className="px-4 py-3 font-mono text-xs text-white/60">{t.date}</td>
                      <td className="px-4 py-3 font-heading text-sm text-secondary-hover font-bold">{t.time}</td>
                      <td className="px-4 py-3 font-heading text-sm text-white/60">{t.event_number}</td>
                      <td className="px-4 py-3">
                        <span className="font-heading text-sm text-white font-bold uppercase">{t.event_name}</span>
                        {t.is_premium && <span className="ml-2 text-[9px] bg-primary-main/20 text-primary-main px-1.5 py-0.5 rounded">PREMIUM</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-primary-main">{t.buyin ? `${t.buyin}` : '-'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-white/50">{t.stack || '-'}</td>
                      <td className="px-4 py-3 font-body text-xs text-white/50">{t.duration || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditForm(t)}
                            className="text-white/40 hover:text-primary-main h-8 w-8 p-0" data-testid={`edit-tournament-${t.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteTournament(t.id)}
                            className="text-white/40 hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-tournament-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tournaments.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-white/30 font-body">No tournaments found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-muted-foreground font-mono">{tournaments.length} tournaments</p>
          </TabsContent>

          {/* ========== SETTINGS TAB ========== */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Branding */}
              <div className="bg-background-surface border border-white/[0.08] rounded-xl p-6" data-testid="branding-settings">
                <h3 className="font-heading text-lg text-white tracking-wide uppercase mb-5">Branding</h3>
                <div className="space-y-4">
                  {[
                    { key: 'title', label: 'Title', placeholder: 'RDPT+' },
                    { key: 'subtitle', label: 'Subtitle', placeholder: 'PLUS' },
                    { key: 'tagline', label: 'Tagline', placeholder: 'Daily Tournament Schedule' },
                    { key: 'date_range', label: 'Date Range', placeholder: 'April 13 - 18, 2026' },
                    { key: 'hashtag', label: 'Hashtag', placeholder: '#rdptplus' },
                    { key: 'brand_name', label: 'Brand Name', placeholder: 'RDPT Plus' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">{label}</Label>
                      <Input value={settings[key] || ''} onChange={e => updateSetting(key, e.target.value)}
                        placeholder={placeholder} className="bg-background-base border-white/10 text-white"
                        data-testid={`settings-${key}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Images */}
              <div className="bg-background-surface border border-white/[0.08] rounded-xl p-6" data-testid="image-settings">
                <h3 className="font-heading text-lg text-white tracking-wide uppercase mb-5">Images</h3>
                <div className="space-y-6">
                  {[
                    { key: 'hero_image_url', label: 'Hero Dragon Image' },
                    { key: 'hero_bg_url', label: 'Hero Background Texture' },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">{label}</Label>
                      <div className="flex gap-2">
                        <Input value={settings[key] || ''} onChange={e => updateSetting(key, e.target.value)}
                          placeholder="Paste image URL or upload" className="bg-background-base border-white/10 text-white flex-1"
                          data-testid={`settings-${key}`} />
                        <Button variant="outline" size="sm" onClick={() => handleImageUpload(key)}
                          disabled={uploadingField === key}
                          className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                          data-testid={`upload-${key}`}>
                          <Upload className="w-4 h-4" />
                        </Button>
                      </div>
                      {settings[key] && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-white/[0.06] h-24 bg-background-base">
                          <img src={settings[key]} alt={label} className="w-full h-full object-cover opacity-80" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Links */}
              <div className="bg-background-surface border border-white/[0.08] rounded-xl p-6 lg:col-span-2" data-testid="social-settings">
                <h3 className="font-heading text-lg text-white tracking-wide uppercase mb-5">Social & Links</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'website_url', label: 'Website URL' },
                    { key: 'facebook_url', label: 'Facebook URL' },
                    { key: 'instagram_url', label: 'Instagram URL' },
                    { key: 'x_url', label: 'X (Twitter) URL' },
                    { key: 'youtube_url', label: 'YouTube URL' },
                    { key: 'pokerlens_url', label: 'PokerLens URL' },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">{label}</Label>
                      <Input value={settings[key] || ''} onChange={e => updateSetting(key, e.target.value)}
                        className="bg-background-base border-white/10 text-white"
                        data-testid={`settings-${key}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Separator className="my-6 bg-white/[0.06]" />

            <Button onClick={saveSettings} disabled={settingsSaving}
              className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider"
              data-testid="save-settings-button">
              <Check className="w-4 h-4 mr-2" /> {settingsSaving ? 'Saving...' : 'Save All Settings'}
            </Button>
          </TabsContent>

          {/* ========== SITE CONTENT TAB ========== */}
          <TabsContent value="content">
            <SiteContentTab token={token} />
          </TabsContent>

          {/* ========== SERIES TAB ========== */}
          <TabsContent value="series">
            <SeriesTab token={token} />
          </TabsContent>

          <TabsContent value="floor">
            <FloorPlanTab token={token} />
          </TabsContent>

          <TabsContent value="icm">
            <IcmTab token={token} />
          </TabsContent>

          {/* ========== VIDEOS TAB ========== */}
          <TabsContent value="videos">
            <VideosTab token={token} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
